<template>
  <section>
    <div class="listview__head">
      <div>
        <div class="c-lbl">Informes</div>
        <div class="listview__desc">Análisis de facturación y descargas a Excel/CSV.</div>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <button class="btn btn-g btn-sm" @click="descargarFacturas" :disabled="downloading">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Descargar facturas
        </button>
        <button class="btn btn-g btn-sm" @click="descargarCuentas" :disabled="downloading">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Descargar cuentas corrientes
        </button>
      </div>
    </div>

    <div class="listview__filters">
      <div>
        <label class="fl">Desde</label>
        <input class="fi" type="date" v-model="filters.desde" @change="fetch" style="max-width:160px" />
      </div>
      <div>
        <label class="fl">Hasta</label>
        <input class="fi" type="date" v-model="filters.hasta" @change="fetch" style="max-width:160px" />
      </div>
      <div style="flex:1;min-width:200px">
        <label class="fl">Cliente</label>
        <select class="fi" v-model.number="filters.cliente_id" @change="fetch">
          <option :value="null">Todos</option>
          <option v-for="c in clientes" :key="c.id" :value="c.id">{{ c.nombre }}</option>
        </select>
      </div>
      <div>
        <label class="fl">Estado</label>
        <select class="fi" v-model="filters.estado" @change="fetch" style="max-width:140px">
          <option value="">Todos</option>
          <option value="pendiente">Pendiente</option>
          <option value="pagada">Pagada</option>
          <option value="vencida">Vencida</option>
          <option value="anulada">Anulada</option>
        </select>
      </div>
      <button class="btn btn-g btn-sm" @click="resetFilters" title="Limpiar filtros" style="align-self:flex-end;margin-bottom:2px">Limpiar</button>
    </div>

    <div class="g-4 mb-16">
      <div class="card card-sm stat-card">
        <div class="stat-lbl">Total facturado</div>
        <div class="stat-val">{{ fmt(data.totales.monto) }}</div>
        <div class="stat-sub">{{ data.totales.total }} facturas</div>
      </div>
      <div class="card card-sm stat-card">
        <div class="stat-lbl">Cobrado</div>
        <div class="stat-val">{{ fmt(data.totales.cobrado) }}</div>
        <div class="stat-sub"><span class="pill pill-g">pagadas</span></div>
      </div>
      <div class="card card-sm stat-card">
        <div class="stat-lbl">Por cobrar</div>
        <div class="stat-val">{{ fmt(data.totales.por_cobrar) }}</div>
        <div class="stat-sub"><span class="pill pill-a">pendientes + vencidas</span></div>
      </div>
      <div class="card card-sm stat-card">
        <div class="stat-lbl">Tasa de cobro</div>
        <div class="stat-val">{{ pctCobro }}%</div>
        <div class="stat-sub"><span class="pill pill-b">cobrado / facturado</span></div>
      </div>
    </div>

    <div class="g-2 mb-16">
      <div class="card card-p">
        <div class="c-lbl mb-8">Facturación mensual</div>
        <div v-if="loading" class="tbl-empty">Cargando...</div>
        <div v-else-if="!data.serie_mensual.length" class="tbl-empty">Sin datos para el filtro.</div>
        <div v-else class="barchart">
          <div v-for="p in data.serie_mensual" :key="p.mes" class="barchart__bar" :title="`${p.mes}: ${fmt(p.m)} (${p.c} facturas)`">
            <div class="barchart__fill" :style="{ height: barHeight(p.m) + '%' }"></div>
            <div class="barchart__lbl">{{ formatMes(p.mes) }}</div>
          </div>
        </div>
      </div>

      <div class="card card-p">
        <div class="c-lbl mb-8">Por estado</div>
        <div v-if="loading" class="tbl-empty">Cargando...</div>
        <div v-else-if="!data.por_estado.length" class="tbl-empty">Sin datos.</div>
        <table v-else class="tbl">
          <thead><tr><th>Estado</th><th style="text-align:right">Cant.</th><th style="text-align:right">Monto</th></tr></thead>
          <tbody>
            <tr v-for="e in data.por_estado" :key="e.estado">
              <td><span class="pill" :class="estadoPill(e.estado)">{{ e.estado }}</span></td>
              <td class="mono" style="text-align:right">{{ e.c }}</td>
              <td class="mono" style="text-align:right">{{ fmt(e.m) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card card-p">
      <div class="c-lbl mb-8">Top 10 clientes por facturación</div>
      <div v-if="loading" class="tbl-empty">Cargando...</div>
      <div v-else-if="!data.top_clientes.length" class="tbl-empty">Sin datos.</div>
      <table v-else class="tbl">
        <thead><tr><th>#</th><th>Cliente</th><th>CUIT</th><th style="text-align:right">Facturas</th><th style="text-align:right">Monto</th></tr></thead>
        <tbody>
          <tr v-for="(t, i) in data.top_clientes" :key="t.cliente_id">
            <td class="mono">{{ i + 1 }}</td>
            <td><strong style="color:var(--tx-hi)">{{ t.cliente?.nombre || '—' }}</strong></td>
            <td class="mono">{{ t.cliente?.cuit ? fmtCuit(t.cliente.cuit) : '—' }}</td>
            <td class="mono" style="text-align:right">{{ t.c }}</td>
            <td class="mono" style="text-align:right"><strong class="grad-text">{{ fmt(t.m) }}</strong></td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import api from '../services/api';
import { format as fmtCuit } from '../utils/cuit';

export default {
  name: 'ReportesView',
  data() {
    return {
      loading: true, downloading: false,
      filters: { desde: '', hasta: '', cliente_id: null, estado: '' },
      clientes: [],
      data: {
        totales: { total: 0, monto: 0, cobrado: 0, por_cobrar: 0 },
        serie_mensual: [], por_mes: [], por_estado: [], top_clientes: []
      },
      fmtCuit
    };
  },
  computed: {
    pctCobro() {
      const m = Number(this.data.totales.monto) || 0;
      const c = Number(this.data.totales.cobrado) || 0;
      return m > 0 ? Math.round((c / m) * 100) : 0;
    }
  },
  async mounted() {
    await Promise.all([this.loadClientes(), this.fetch()]);
  },
  methods: {
    async loadClientes() {
      try {
        const { data } = await api.get('/clientes', { params: { per_page: 200 } });
        this.clientes = data.data.data;
      } catch (e) { if (!e?.isAuthExpired) console.warn('[Reportes clientes]', e); }
    },
    async fetch() {
      this.loading = true;
      try {
        const params = {};
        if (this.filters.desde)     params.desde = this.filters.desde;
        if (this.filters.hasta)     params.hasta = this.filters.hasta;
        if (this.filters.cliente_id) params.cliente_id = this.filters.cliente_id;
        if (this.filters.estado)    params.estado = this.filters.estado;
        const { data } = await api.get('/reportes/facturacion', { params });
        this.data = data;
      } catch (e) {
        if (!e?.isAuthExpired) console.warn('[Reportes]', e);
      } finally { this.loading = false; }
    },
    resetFilters() {
      this.filters = { desde: '', hasta: '', cliente_id: null, estado: '' };
      this.fetch();
    },
    barHeight(m) {
      const max = Math.max(...this.data.serie_mensual.map(p => Number(p.m) || 0), 1);
      return Math.max(4, (Number(m) / max) * 100);
    },
    formatMes(mes) {
      if (!mes) return '—';
      const [y, m] = mes.split('-');
      return `${['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][Number(m)-1]} ${y.slice(2)}`;
    },
    estadoPill(e) { return ({ pendiente: 'pill-a', pagada: 'pill-g', vencida: 'pill-r', anulada: 'pill-b' })[e] || 'pill-b'; },
    fmt(n, m='ARS') { return new Intl.NumberFormat('es-AR', { style:'currency', currency:m, maximumFractionDigits:0 }).format(Number(n||0)); },

    async descargarFacturas() { await this.descargar('/reportes/facturas/export', 'facturas'); },
    async descargarCuentas()  { await this.descargar('/reportes/cuentas/export', 'cuentas_corrientes'); },

    async descargar(url, nombre) {
      this.downloading = true;
      try {
        const params = {};
        if (this.filters.desde)      params.desde = this.filters.desde;
        if (this.filters.hasta)      params.hasta = this.filters.hasta;
        if (this.filters.cliente_id) params.cliente_id = this.filters.cliente_id;
        if (this.filters.estado)     params.estado = this.filters.estado;
        const resp = await api.get(url, { params, responseType: 'blob' });
        const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `mhlm_${nombre}_${new Date().toISOString().slice(0,10)}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (e) {
        if (!e?.isAuthExpired) alert('No se pudo descargar: ' + (e?.response?.data?.message || e.message));
      } finally { this.downloading = false; }
    }
  }
};
</script>

<style scoped>
.barchart{ display:flex; gap:6px; align-items:flex-end; height:180px; padding:12px 4px 0; }
.barchart__bar{
  flex:1; display:flex; flex-direction:column; align-items:center; gap:6px;
  min-width:28px; cursor:default;
}
.barchart__fill{
  width:100%; min-height:4px;
  background:var(--mhlm-grad);
  border-radius:4px 4px 0 0;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.25);
  transition:opacity .15s;
}
.barchart__bar:hover .barchart__fill{ opacity:.85; }
.barchart__lbl{ font-size:10.5px; color:var(--tx-lo); white-space:nowrap; }
</style>
